import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth, googleProvider } from '../lib/firebase';
import api from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Handle Google redirect result on page load
  useEffect(() => {
    getRedirectResult(auth).catch(() => {});
  }, []);

  // Sync Firebase auth state and fetch user profile from backend
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);
      if (fbUser) {
        try {
          const token = await fbUser.getIdToken();
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const response = await api.get('/auth/me');
          setUser(response.data);
        } catch (error) {
          if (error.response?.status === 404 || error.response?.status === 401) {
            // User exists in Firebase but not in our DB — auto-register them
            try {
              const regResponse = await api.post('/auth/register', {
                email: fbUser.email,
                name: fbUser.displayName || fbUser.email?.split('@')[0] || '',
                firebase_uid: fbUser.uid,
                profile_photo: fbUser.photoURL || '',
              });
              setUser(regResponse.data);
            } catch (regError) {
              console.error('Auto-registration failed:', regError);
              setUser(null);
            }
          } else {
            console.error('Failed to fetch user profile:', error);
            setUser(null);
          }
        }
      } else {
        setUser(null);
        delete api.defaults.headers.common['Authorization'];
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Refresh the token periodically and on API calls
  useEffect(() => {
    const interceptor = api.interceptors.request.use(async (config) => {
      if (firebaseUser) {
        try {
          const token = await firebaseUser.getIdToken();
          config.headers.Authorization = `Bearer ${token}`;
        } catch (e) {
          // Token refresh failed — user will be redirected to login
        }
      }
      return config;
    });
    return () => api.interceptors.request.eject(interceptor);
  }, [firebaseUser]);

  const login = async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const token = await cred.user.getIdToken();
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    const response = await api.get('/auth/me');
    setUser(response.data);
    return response.data;
  };

  const register = async (email, password, name) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    const token = await cred.user.getIdToken();
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    // Create user profile in backend
    const response = await api.post('/auth/register', {
      email,
      name,
      firebase_uid: cred.user.uid,
    });
    setUser(response.data);
    return response.data;
  };

  const loginWithGoogle = async () => {
    try {
      const cred = await signInWithPopup(auth, googleProvider);
      // Wait for onAuthStateChanged to fetch/create user profile
      const token = await cred.user.getIdToken();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Fetch or create user profile directly
      try {
        const response = await api.get('/auth/me');
        setUser(response.data);
        return response.data;
      } catch (err) {
        // New user — register
        const response = await api.post('/auth/register', {
          email: cred.user.email,
          name: cred.user.displayName || cred.user.email?.split('@')[0] || '',
          firebase_uid: cred.user.uid,
          profile_photo: cred.user.photoURL || '',
        });
        setUser(response.data);
        return response.data;
      }
    } catch (error) {
      if (error.code === 'auth/popup-blocked' || error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
        await signInWithRedirect(auth, googleProvider);
        return null;
      }
      throw error;
    }
  };

  const resetPassword = async (email) => {
    await sendPasswordResetEmail(auth, email);
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setFirebaseUser(null);
    delete api.defaults.headers.common['Authorization'];
  };

  const updateProfile = async (profileData) => {
    const response = await api.put('/users/profile', profileData);
    setUser(response.data);
    return response.data;
  };

  const value = {
    user,
    firebaseUser,
    loading,
    login,
    register,
    loginWithGoogle,
    resetPassword,
    logout,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
